import React, { useEffect, useState } from "react";

const OrderHistory = ({ user, token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user || !token) return;
    const kdnnr = user.kdnnr || (user.all_fields && user.all_fields['ASP::ID.KDN']);
    if (!kdnnr) {
      setError('Fehler: Kundennummer (ASP::ID.KDN) fehlt im Benutzerobjekt!');
      return;
    }
    setLoading(true);
    const API = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/order-history?kdnnr=${kdnnr}`, { 
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.text())
      .then((data) => {
        // Parse HTML and extract rows
        const parser = new DOMParser();
        const doc = parser.parseFromString(data, "text/html");
        const trs = Array.from(doc.querySelectorAll("tbody tr"));
        // Group by Bestellnummer
        const grouped = {};
        trs.forEach(tr => {
          const tds = tr.querySelectorAll("td");
          // Now expecting 5 columns: bst, datum, extra, produkt, menge
          if (tds.length === 5) {
            const bst = tds[0].textContent;
            const datum = tds[1].textContent;
            const extra = tds[2].textContent;
            const produkt = tds[3].textContent;
            const menge = tds[4].textContent;
            if (!grouped[bst]) {
              grouped[bst] = { datum, produkte: [] };
            }
            grouped[bst].produkte.push({ extra, produkt, menge });
          }
        });
        setRows(grouped);
        setLoading(false);
      })
      .catch(() => {
        setError("Fehler beim Laden der Bestellhistorie.");
        setLoading(false);
      });
  }, [user, token]);

    // Animation: always render the card, animate max-height
    if (!user)
      return (
        <div className="bg-white rounded-lg shadow p-4 max-w-5xl mx-auto my-8 text-center text-gray-700">
          Bitte einloggen, um die Bestellhistorie zu sehen.
        </div>
      );
    // Card with animated grow
    return (
      <div
        className="bg-white rounded-lg shadow p-4 max-w-5xl mx-auto my-8 text-center text-gray-700 transition-all duration-700 overflow-hidden"
        style={{
          maxHeight: loading ? 80 : error ? 120 : 2000,
        }}
      >
        {loading ? (
            <div className="text-left">
              <div className="max-w-5xl mx-auto mt-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Bestellhistorie</h2>
          </div><span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              Lade Bestellhistorie...
            </span></div>
        ) : error ? (
          <span className="text-red-600">{error}</span>
        ) : (
          <div className="text-left">
            <div className="max-w-5xl mx-auto mt-2">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Bestellhistorie</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="order-history-table w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border-b-2 border-gray-300 py-1 text-left">Bestellnummer</th>
                    <th className="border-b-2 border-gray-300 py-1 text-left">Bestelldatum</th>
                    <th className="border-b-2 border-gray-300 py-1 text-left">Besteller</th>
                    <th className="border-b-2 border-gray-300 py-1 text-left">Produkt</th>
                    <th className="border-b-2 border-gray-300 py-1 text-left">Menge</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(rows).map(([bst, { datum, produkte }], idx, arr) => (
                    <React.Fragment key={bst}>
                      {produkte.map((p, pIdx) => (
                        <tr
                          key={bst + '-' + pIdx}
                          className={
                            pIdx === 0 && idx === 0
                              ? "border-t-2 border-gray-300"
                              : ""
                          }
                        >
                          {/* Nur beim ersten Produkt die Bestellnummer und Datum anzeigen */}
                          {pIdx === 0 ? (
                            <>
                              <td
                                className={
                                  `font-bold align-top ${pIdx === 0 && idx === 0 ? "" : ""}`
                                }
                                rowSpan={produkte.length}
                              >
                                {bst}
                              </td>
                              <td
                                className={
                                  `align-top ${pIdx === 0 && idx === 0 ? "" : ""}`
                                }
                                rowSpan={produkte.length}
                              >
                                {datum}
                              </td>
                              <td
                                className={
                                  `whitespace-nowrap align-top ${pIdx === 0 && idx === 0 ? "" : ""}`
                                }                                
                                rowSpan={produkte.length}
                              >
                                {p.extra}
                              </td>
                              <td className={`${pIdx === 0 && idx === 0 ? "" : ""}`}>{p.produkt.replace(/\s*\([^)]*\)/g, '')}</td>
                              <td className={`text-right ${pIdx === 0 && idx === 0 ? "" : ""}`}>
                                {typeof p.menge === 'string' && p.menge.match(/[.,]\d{3}/) ? p.menge : Number(p.menge).toLocaleString('de-DE')}
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{p.produkt.replace(/\s*\([^)]*\)/g, '')}</td>
                              <td className="text-right">{typeof p.menge === 'string' && p.menge.match(/[.,]\d{3}/) ? p.menge : Number(p.menge).toLocaleString('de-DE')}</td>
                            </>
                          )}
                        </tr>
                      ))}
                      {/* Linie zwischen den Bestellungen, außer nach der letzten */}
                      {idx < arr.length && (
                        <tr>
                          <td colSpan={5}>
                            <hr className="border-t-2 border-gray-300" />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {Object.keys(rows).length === 0 && (
                <div className="text-center py-6 text-gray-500">Keine Bestellungen vorhanden.</div>
              )}
            </div>
          </div>
        )}
      </div>
    );

};

export default OrderHistory;